import { useEffect, useRef, useState } from "react";
import type { TaskData } from "@/components/TaskRow";
import { consumeTaskDeepLink } from "@/lib/taskLink";

export function useSelectedRootTask(pendingTasks: TaskData[], completedTasks: TaskData[] | null) {
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const deepLinkRef = useRef<string | null>(null);

  useEffect(() => {
    const taskId = consumeTaskDeepLink();
    if (!taskId) return;
    const found = pendingTasks.find((t) => t.id === taskId);
    if (found) {
      setSelectedTask(found);
      return;
    }
    deepLinkRef.current = taskId;
    fetch(`/tasks/${taskId}`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (payload?.task) setSelectedTask(payload.task);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Subtasks are not among the page's rows; DetailsPanel keeps them fresh itself.
    if (!selectedTask || (selectedTask.parentId ?? null) !== null) return;
    const fresh = [...pendingTasks, ...(completedTasks ?? [])].find((t) => t.id === selectedTask.id);
    if (!fresh) {
      // A deep-linked task outside the loaded rows (completed, nested) stays open.
      if (deepLinkRef.current === selectedTask.id) return;
      setSelectedTask(null);
    } else if (fresh !== selectedTask) {
      setSelectedTask(fresh);
    }
  }, [pendingTasks, completedTasks, selectedTask]);

  function selectTask(task: TaskData | null) {
    deepLinkRef.current = null;
    setSelectedTask(task);
  }

  return { selectedTask, selectTask };
}
